import { logAnalyticsEvent } from "../firebase/firebaseConfig";
import safeImageUrl from "./safeImageUrl";
import posthog from "posthog-js";

const environment = process.env.REACT_APP_ENV || "production";

async function heapTrackWithRetry(event, data, retry = true) {
  if (window.heap == null) {
    if (!retry) return;

    return setTimeout(() => {
      heapTrackWithRetry(event, data, false);
    }, 2000);
  };
  try {
    return window.heap.track(event, data);
  } catch (error) {
    console.warn(`Failed to track facebook event ${event}`)
  }
}

async function identifyHeap(user, retry = true) {
  if (window.heap == null) {
    if (!retry) return;

    return setTimeout(() => {
      identifyHeap(user, false);
    }, 2000);
  }

  try {
    window.heap.identify(user.uid)
    return window.heap.addUserProperties({
      email: user.email,
      displayName: user.displayName,
      createdAt: Date(user.createdAt.seconds),
      provider: user.providerId,
      uid: user.uid,
      photoUrl: user.photoUrl,
      phoneNumber: user.phoneNumber,
    });
  } catch (error) {
    console.warn(`Failed to identify user ${user.uid}`)
  }
};

async function tikTokTrackWithRetry(event, data, retry = true) {
  if (window.ttq == null) {
    if (!retry) return;

    return setTimeout(() => {
      tikTokTrackWithRetry(event, data, false);
    }, 2000);
  };
  try {
    return window.ttq.track(event, data);
  } catch (error) {
    console.warn(`Failed to track facebook event ${event}`)
  }
}

async function facebookTrackWithRetry(event, data, retry = true) {
  if (window.fbq == null) {
    if (!retry) return;

    return setTimeout(() => {
      facebookTrackWithRetry(event, data, false);
    }, 2000);
  };
  try {
    return window.fbq('track', event, data);
  } catch (error) {
    console.warn(`Failed to track facebook event ${event}`)
  }
}

async function klaviyoTrackWithRetry(event, data, retry = true) {
  if (window.klaviyo == null) {
    if (!retry) return;

    return setTimeout(() => {
      klaviyoTrackWithRetry(event, data, false);
    }, 2000);
  };

  if (window.location.host.match("localhost")) {
    return
  }

  try {
    return window.klaviyo.track(event, data);
  } catch (error) {
    console.warn(`Failed to track event ${event}`)
  }
}

async function identifyKlaviyo(user, callback, retry = true) {
  if (window.klaviyo == null) {
    if (!retry) return;

    return setTimeout(() => {
      identifyKlaviyo(user, callback, false);
    }, 2000);
  }

  if (window.location.host.match("localhost")) {
    return
  }

  try {
    return window.klaviyo.identify({
      email: user.email,
      displayName: user.displayName,
      createdAt: Date(user.createdAt.seconds),
      provider: user.providerId,
      uid: user.uid,
      photoUrl: user.photoUrl,
      phoneNumber: user.phoneNumber,
    }, callback);
  } catch (error) {
    console.warn(`Failed to identify user ${user.uid}`)
  }
};

async function identifyTikTok(user, retry = true) {
  if (window.ttq == null) {
    if (!retry) return;

    return setTimeout(() => {
      identifyTikTok(user, false);
    }, 2000);
  }

  try {
    return window.ttq.identify({
      email: user.email,
      external_id: user.uid,
    });
  } catch (error) {
    console.warn(`Failed to identify user ${user.uid}`)
  }
};

async function identify(user, callback) {
  identifyKlaviyo(user, callback);
  identifyTikTok(user);
  identifyHeap(user);

  try {
    posthog.identify(user.uid);
    posthog.register({
      email: user.email,
      displayName: user.displayName,
      provider: user.providerId,
      uid: user.uid,
    });
  } catch (error) {
    console.warn(`PostHog identify failed for user ${user.uid}`);
  }
}

async function purchase(data) {
  const eventData = {
    page_title: window.document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    environment,
    ...data,
  };
  logAnalyticsEvent("purchase", eventData);

  posthog.capture("purchase", eventData);

  heapTrackWithRetry('Purchase', data);
}

async function addToCart(data) {
  const eventData = {
    page_title: window.document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    environment,
    ...data,
  };
  logAnalyticsEvent("add_to_cart", eventData);

  posthog.capture("add_to_cart", eventData);

  tikTokTrackWithRetry('AddToCart', {
    contents: [{
      content_id: data.product_id,
      content_type: "product",
      content_name: data.product_name,
      price: data.price.finalPrice,
      brand: data.brand_name,
    }],
  });

  facebookTrackWithRetry('AddToCart', {
    content_ids: [data.product_id],
    currency: 'USD',
    value: data.price.finalPrice,
  });

  heapTrackWithRetry('Added to Cart', data);
  return klaviyoTrackWithRetry('Added to Cart', data);
}

async function productViewed(product) {
  const eventData = {
    page_title: window.document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    environment,
    ...product,
  };
  logAnalyticsEvent('product_view', eventData);

  posthog.capture('product_view', eventData);

  tikTokTrackWithRetry('ViewContent', {
    contents: [{
      content_id: product.ProductID,
      content_type: "product",
      content_name: product.ProductName,
      content_category: product.Categories[0],
      price: product.Price,
      brand: product.Brand,
    }],
  });

  facebookTrackWithRetry('ViewContent', {
    content_ids: [product.ProductID],
  })

  heapTrackWithRetry("Viewed Product", product);
  return klaviyoTrackWithRetry("Viewed Product", product);
}

async function viewCart() {
  const eventData = {
    page_title: window.document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    environment,
  };

  logAnalyticsEvent('cart_view', eventData);
  posthog.capture('cart_view', eventData);
}

async function addPaymentInfo({ cartItems, summary, cartId, wallet }) {
  const data = {
    content_type: "checkout_button",
    page_title: window.document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    environment,
    cart_payment_method: "Stripe",
    cart_sooperstock_ID: cartId,
    cart_items: cartItems.length,
    cart_subtotal: summary.itemTotal,
    cart_shipping: summary.calculatedShippingCost,
    cart_discount: summary.discountAmount,
    cart_wallet_balance: wallet.balance,
    cart_total: summary.paymentTotals.cc,
  };

  logAnalyticsEvent("checkout_stripe_button", data);
  posthog.capture("checkout_stripe_button", data);

  facebookTrackWithRetry('AddPaymentInfo', {
    value: summary.paymentTotals.cc,
  });

  heapTrackWithRetry("AddPaymentInfo", data);

  tikTokTrackWithRetry('AddPaymentInfo', {
    value: summary.total,
    currency: 'USD',
    contents: cartItems.map((cartItem) => ({
      quantity: cartItem.quantity,
      price: cartItem.final_price,
      content_id: cartItem.product_id,
      content_name: cartItem.product_name,
    })),
  });

}

async function startedCheckout({ cartItems, summary, wallet, cart_sooperstock_ID }) {
  const data = {
    $event_id: `${cart_sooperstock_ID}_${Date.now()}`,
    $value: summary.total,
    CheckoutURL: `${window.location.origin}/cart?id=${cart_sooperstock_ID}`,
    ItemNames: cartItems.map(({ product_name }) => product_name),
    Categories: [],
    Items: cartItems.map((cartItem) => ({
      ProductID: cartItem.product_id,
      SKU: cartItem.id,
      ProductName: cartItem.product_name,
      Quantity: cartItem.quantity,
      ItemPrice: cartItem.price.finalPrice,
      RowTotal: cartItem.final_price * cartItem.quantity,
      ProductURL: `${window.location.origin}/Listing/${cartItem.id}`,
      ImageURL: safeImageUrl(cartItem.images?.[0]),
      ProductCategories: []
    })),
  }

  logAnalyticsEvent("checkout_view", {
    content_type: "checkout_page",
    page_title: window.document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    environment,
    cart_id: cart_sooperstock_ID,
    cart_items: data.Items.length,
    cart_subtotal: summary.subtotal,
    cart_shipping: summary.calculatedShippingCost,
    cart_discount: summary.discountAmount,
    cart_wallet_balance: wallet.balance,
    cart_total: summary.total,
  });

  posthog.capture("checkout_view", {
    content_type: "checkout_page",
    page_title: window.document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    environment,
    cart_id: cart_sooperstock_ID,
    cart_items: data.Items.length,
    cart_subtotal: summary.subtotal,
    cart_shipping: summary.calculatedShippingCost,
    cart_discount: summary.discountAmount,
    cart_wallet_balance: wallet.balance,
    cart_total: summary.total,
  });

  tikTokTrackWithRetry('InitiateCheckout', {
    value: summary.total,
    currency: 'USD',
    contents: cartItems.map((cartItem) => ({
      quantity: cartItem.quantity,
      price: cartItem.final_price,
      content_id: cartItem.product_id,
      content_name: cartItem.product_name,
    })),
  });

  facebookTrackWithRetry('InitiateCheckout', {
    value: summary.paymentTotals.cc,
    content_ids: cartItems.map((cartItem) => cartItem.product_id),
    num_items: summary.itemCount,
  })

  heapTrackWithRetry("Started Checkout", data);
  return klaviyoTrackWithRetry("Started Checkout", data);
}

async function addedAddress(address) {
  const eventData = {
    page_title: window.document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
    environment,
  };

  logAnalyticsEvent('added_address', eventData);
  posthog.capture('added_address', eventData);

  heapTrackWithRetry("Address Added", address);
  return klaviyoTrackWithRetry("Address Added", address);
}

export default {
  addPaymentInfo,
  addedAddress,
  addToCart,
  identify,
  productViewed,
  startedCheckout,
  viewCart,
  purchase,
};
